import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MessageCircle, Mail } from 'lucide-react';
import { PublicHeader } from '@/components/Layout/PublicHeader';
import { PublicFooter } from '@/components/Layout/PublicFooter';
import { HelpSearchBar, CategoryCard } from '@/components/help';
import { helpService, HelpCategory, HelpArticle, stripMarkdown } from '@/services/helpService';
import { Spinner } from '@/components/ui/Spinner';

export function HelpHome() {
  const [categories, setCategories] = useState<HelpCategory[]>([]);
  const [popularArticles, setPopularArticles] = useState<HelpArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [cats, popular] = await Promise.all([
          helpService.getCategories(),
          helpService.getPopularArticles(),
        ]);
        setCategories(cats);
        setPopularArticles(popular);
      } catch (error) {
        console.error('Failed to load help center data:', error);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="flex justify-center items-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Help Center | Cascady</title>
        <meta name="description" content="Find answers to your questions about Cascady. Browse our help articles, tutorials, and guides to get the most out of your AI image and video generation workflow." />
        <meta property="og:title" content="Help Center | Cascady" />
        <meta property="og:description" content="Find answers to your questions about Cascady. Browse our help articles, tutorials, and guides." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cascady.anacreon.ai/help" />
      </Helmet>
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative">
        {/* Background gradient */}
        <div className="absolute inset-0 opacity-20 pointer-events-none overflow-hidden">
          <div className="absolute -left-[400px] top-[50px] w-[800px] h-[800px] rounded-full bg-violet-400/30 blur-3xl" />
          <div className="absolute -right-[400px] top-[50px] w-[800px] h-[800px] rounded-full bg-purple-400/30 blur-3xl" />
        </div>

        <div className="container mx-auto px-4 max-w-5xl relative z-10 pt-10 sm:pt-16 pb-8 sm:pb-12">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-semibold text-gray-900 mb-3 sm:mb-4">
              How can we help you?
            </h1>
            <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Search our knowledge base or browse categories below to find answers
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative z-20">
            <HelpSearchBar size="large" autoFocus />
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="container mx-auto px-4 max-w-5xl py-8 sm:py-12 relative z-0">
        <h2 className="text-lg sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6">
          Browse by Category
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {categories.map((category) => (
            <CategoryCard key={category.slug} category={category} />
          ))}
        </div>
      </section>

      {/* Popular Articles */}
      {popularArticles.length > 0 && (
        <section className="container mx-auto px-4 max-w-5xl py-8 sm:py-12 border-t border-gray-100">
          <h2 className="text-lg sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6">
            Popular Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {popularArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/help/${article.category}/${article.slug}`}
                className="group flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl border border-gray-200 bg-white hover:border-violet-300 hover:shadow-sm transition-all"
              >
                <div className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-sm sm:text-base text-gray-900 group-hover:text-violet-600 transition-colors">
                  {stripMarkdown(article.title)}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section className="container mx-auto px-4 max-w-5xl py-8 sm:py-12 border-t border-gray-100">
        <div className="text-center">
          <h2 className="text-lg sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">
            Still need help?
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1d2939] text-white rounded-xl font-medium hover:bg-[#1d2939]/90 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Contact Support
            </Link>
            <a
              href="mailto:support@cascady.com"
              className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
            >
              <Mail className="w-5 h-5" />
              Email Us
            </a>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
